import { useEffect, useMemo, useRef, useState } from 'react';
import { call } from '../../lib/backbarApi';
import { ConfirmButton, ErrorNote, Field, Note } from '../ui';
import { type BlockInfo, type ImageRow, STATUS_LABEL } from './data';
import { classifyKind, composePrompt, KIND_LABEL, neutralize, type IllustrationKind } from './recipe';
import { formatBytes, prepareImage, type Prepared } from './imageTools';
import { StatusPill, Thumb } from './IllustrationCard';

type UploadUrl = { path: string; signed_url: string; token: string; content_type: string };

export function IllustrationDrawer({
  row, moduleTitle, block, version, onClose, onRowChange,
}: {
  row: ImageRow;
  moduleTitle: string;
  block: BlockInfo | null;
  version: string | number | null | undefined;
  onClose: () => void;
  onRowChange: (row: ImageRow, version?: string | number) => void;
}) {
  // ---- 1. Build the prompt ------------------------------------------------
  const [shows, setShows] = useState('');
  const [labels, setLabels] = useState('');
  const [mustNot, setMustNot] = useState('');
  const [closeup, setCloseup] = useState(false);
  const [copied, setCopied] = useState(false);
  const [markError, setMarkError] = useState<unknown>(null);
  const [marked, setMarked] = useState(false);

  useEffect(() => {
    // Prefill from the block when it arrives; never overwrite what Leah typed.
    if (block && !shows) setShows(neutralize(block.brief || block.caption || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block]);

  const kind: IllustrationKind = closeup ? 'CLOSEUP' : (() => {
    const k = classifyKind(shows || block?.brief || '');
    return k === 'CLOSEUP' ? 'PHOTOGRAPH' : k;
  })();
  const prompt = useMemo(
    () => composePrompt({ filename: row.filename, kind, shows, labels, mustNot }),
    [row.filename, kind, shows, labels, mustNot],
  );

  async function copyPrompt() {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Older browsers: leave the textarea selected so a manual copy works.
      promptRef.current?.select();
    }
  }
  const promptRef = useRef<HTMLTextAreaElement>(null);

  async function addToWorkOrder() {
    setMarkError(null);
    try {
      if (row.status === 'placed') {
        const updated = await call<ImageRow>('images.mark', { filename: row.filename, status: 'redo' });
        onRowChange({ ...row, ...updated, status: 'redo' });
      }
      setMarked(true);
    } catch (err) {
      setMarkError(err);
    }
  }

  // ---- 2. Drop the file ---------------------------------------------------
  const [prepared, setPrepared] = useState<Prepared | null>(null);
  const [prepError, setPrepError] = useState<unknown>(null);
  const [uploadError, setUploadError] = useState<unknown>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedAt, setUploadedAt] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const canUpload = Boolean(row.module_id && row.lesson_id) && row.status !== 'retired';

  async function takeFile(file: File | undefined) {
    if (!file) return;
    setPrepError(null);
    setUploadError(null);
    if (prepared) URL.revokeObjectURL(prepared.previewUrl);
    setPrepared(null);
    try {
      setPrepared(await prepareImage(file, row.filename));
    } catch (err) {
      setPrepError(err);
    }
  }

  async function upload() {
    if (!prepared) return;
    setUploading(true);
    setUploadError(null);
    try {
      const signed = await call<UploadUrl>('images.upload_url', {
        filename: row.filename,
        content_type: prepared.contentType,
      });
      const put = await fetch(signed.signed_url, {
        method: 'PUT',
        headers: { 'Content-Type': prepared.contentType, 'x-upsert': 'true' },
        body: prepared.blob,
      });
      if (!put.ok) throw new Error(`The upload did not finish (status ${put.status}).`);
      const committed = await call<ImageRow & { public_url?: string }>('images.commit', {
        filename: row.filename,
        module_id: row.module_id,
        lesson_id: row.lesson_id,
        block_id: row.block_id ?? '',
        width: prepared.width,
        height: prepared.height,
        bytes: prepared.bytes,
        prompt_used: shows.trim() ? prompt : undefined,
      });
      const stamp = Date.now();
      setUploadedAt(stamp);
      onRowChange({ ...row, ...committed, status: 'placed' }, stamp);
      URL.revokeObjectURL(prepared.previewUrl);
      setPrepared(null);
    } catch (err) {
      setUploadError(err);
    } finally {
      setUploading(false);
    }
  }

  // ---- 3. Retire ----------------------------------------------------------
  const [retireError, setRetireError] = useState<unknown>(null);
  async function retire() {
    setRetireError(null);
    try {
      const updated = await call<ImageRow>('images.mark', { filename: row.filename, status: 'retired' });
      onRowChange({ ...row, ...updated, status: 'retired' });
    } catch (err) {
      setRetireError(err);
    }
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const lesson = block?.lessonTitle || row.lesson_id || '';

  return (
    <div className="il-drawer-wrap" role="dialog" aria-modal="true" aria-label={row.filename}>
      <div className="il-scrim" onClick={onClose} />
      <aside className="il-drawer">
        <button type="button" className="lp-back" onClick={onClose}>
          <span aria-hidden="true">‹</span> Back
        </button>

        <div className="il-drawer-head">
          <Thumb row={row} version={version ?? uploadedAt} large />
          <div>
            <code className="il-filename il-filename--big">{row.filename}</code>
            <div className="il-card-where">
              {moduleTitle || row.module_id || 'Not placed in a lesson'}
              {lesson ? <> › {lesson}</> : null}
              {block?.section ? <> › {block.section}</> : null}
            </div>
            <div className="il-drawer-meta">
              <StatusPill status={row.status} />
              {row.width && row.height ? (
                <span className="il-meta-num">{row.width} × {row.height}</span>
              ) : null}
              {row.bytes ? <span className="il-meta-num">{formatBytes(row.bytes)}</span> : null}
            </div>
          </div>
        </div>
        {block?.caption ? <p className="il-drawer-caption">{block.caption}</p> : null}

        {/* ---------------------------------------------------------------- */}
        <section className="il-section">
          <h2 className="lp-section-title">Build the prompt</h2>
          <p className="il-help">
            Paste the finished prompt into ChatGPT, save the picture it makes, then drop the file below.
            The house look is written into the prompt; you only describe the subject.
          </p>
          <Field label="What it shows">
            <textarea className="lp-ta" value={shows} onChange={(e) => setShows(e.target.value)} rows={5} />
          </Field>
          <Field label="Labels that must appear" hint="Comma-separated, spelled exactly as they should print.">
            <input className="lp-inp" value={labels} onChange={(e) => setLabels(e.target.value)} />
          </Field>
          <Field label="Must not include">
            <input className="lp-inp" value={mustNot} onChange={(e) => setMustNot(e.target.value)} />
          </Field>
          <label className="il-check">
            <input type="checkbox" checked={closeup} onChange={(e) => setCloseup(e.target.checked)} />
            <span>This is a close-up of real skin or scalp</span>
          </label>
          <div className="il-kind">{KIND_LABEL[kind]}</div>
          <textarea ref={promptRef} className="lp-ta il-prompt" value={prompt} readOnly rows={9} aria-label="The finished prompt" />
          <div className="lp-row">
            <button type="button" className="lp-btn lp-btn--primary" onClick={() => void copyPrompt()}>
              {copied ? 'Copied' : 'Copy prompt'}
            </button>
            <a className="lp-btn" href="https://chatgpt.com" target="_blank" rel="noopener noreferrer">
              Open ChatGPT
            </a>
            <button
              type="button"
              className="lp-btn"
              disabled={marked || row.status === 'missing' || row.status === 'redo' || row.status === 'retired'}
              onClick={() => void addToWorkOrder()}
            >
              {row.status === 'missing' || row.status === 'redo' || marked ? 'On the work order' : 'Add to work order'}
            </button>
          </div>
          <ErrorNote error={markError} />
        </section>

        {/* ---------------------------------------------------------------- */}
        <section className="il-section">
          <h2 className="lp-section-title">Drop the file</h2>
          {!canUpload && row.status === 'retired' && (
            <Note>This illustration is retired. Nothing in the curriculum shows it.</Note>
          )}
          {!canUpload && row.status !== 'retired' && (
            <Note>No lesson names this file, so there is nowhere to place it. Add the illustration to a lesson first.</Note>
          )}
          {canUpload && (
            <>
              <div
                className={dragOver ? 'il-drop il-drop--over' : 'il-drop'}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  void takeFile(e.dataTransfer.files?.[0]);
                }}
              >
                <p>Drag the picture here, or</p>
                <button type="button" className="lp-btn" onClick={() => fileInput.current?.click()}>
                  Choose a file
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    void takeFile(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
                <p className="il-help">It is resized to 1600 px and saved as {row.filename} in the lesson.</p>
              </div>
              <ErrorNote error={prepError} />
              {prepared && (
                <div className="il-preview">
                  <img src={prepared.previewUrl} alt="" />
                  <div>
                    <div className="il-meta-num">
                      {prepared.width} × {prepared.height} · {formatBytes(prepared.bytes)}
                    </div>
                    {!prepared.parchment && <Note>This doesn't look like our usual style.</Note>}
                    <div className="lp-row">
                      <button type="button" className="lp-btn lp-btn--primary" disabled={uploading} onClick={() => void upload()}>
                        {uploading ? 'Placing…' : row.status === 'placed' ? 'Replace the picture' : 'Place the picture'}
                      </button>
                      <button
                        type="button"
                        className="lp-btn lp-btn--quiet"
                        disabled={uploading}
                        onClick={() => {
                          URL.revokeObjectURL(prepared.previewUrl);
                          setPrepared(null);
                        }}
                      >
                        Never mind
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <ErrorNote error={uploadError} />
              {uploadedAt && !prepared && <Note tone="good">Placed. The lesson shows the new picture.</Note>}
            </>
          )}
        </section>

        {/* ---------------------------------------------------------------- */}
        {row.status !== 'retired' && (
          <section className="il-section">
            <h2 className="lp-section-title">Retire</h2>
            <p className="il-help">
              Use this when the lesson no longer needs the picture. The file stays in storage; the ledger stops asking for it.
            </p>
            <ConfirmButton
              label="Retire this illustration"
              sentence={`Retire ${row.filename}? It is ${STATUS_LABEL[row.status].toLowerCase()} now and will drop off the work order.`}
              yes="Yes, retire it"
              tone="danger"
              onConfirm={retire}
            />
            <ErrorNote error={retireError} />
          </section>
        )}
      </aside>
    </div>
  );
}
